/** The site-by-month coverage grid for a dashboard band (ADR-0212, P7): did every bill arrive?
 *
 * This is `CoverageHeatmap` (a real `role="grid"`, roving tab stop, arrow keys, one pattern and one
 * word per state) driven by sites and months, with the one thing a band needs added: **the tooltip
 * says what is missing**. For a `missing` cell it names the source that was expected and the month
 * (`Electricity bill for Leeds, March 2025: nothing arrived and nothing was declared`); for an
 * `unknown` cell it says why the product cannot tell. A caller's own `note` always wins.
 *
 * **The six census states are never merged** - they are the heatmap's, in the heatmap's order, and
 * `received` is the census's `submitted`. A pending month is "not due yet", not missing. Nothing is
 * counted or inferred here; a cell is the state it was handed. A cell with a `receipt` opens it on
 * click or Enter when the caller passes `onOpenReceipt`; cells without one stay readable only.
 */

import { CoverageHeatmap } from "./CoverageHeatmap";
import type { CoverageColumn } from "./CoverageHeatmap";
import { COVERAGE_MEANING } from "./coverage";
import type { CoverageState } from "./coverage";
import type { OpenReceipt } from "./receipt";

export interface CoverageSiteCell {
  state: CoverageState;
  /** Extra words for the tooltip; replaces the default sentence for this cell. */
  note?: string;
  /** Where the bill's or declaration's receipt is. */
  receipt?: string;
}

export interface CoverageSite {
  id: string;
  label: string;
  /** The source this row is a stream of: `Electricity bill`. Named in the "missing" tooltip. */
  expects: string;
  /** One per month, in month order. */
  cells: readonly CoverageSiteCell[];
}

export function CoverageGrid({
  sites,
  months,
  caption,
  onOpenReceipt,
  testId = "coverage-grid",
}: {
  sites: readonly CoverageSite[];
  months: readonly CoverageColumn[];
  caption: string;
  onOpenReceipt?: OpenReceipt;
  testId?: string;
}) {
  const noteFor = (site: CoverageSite, cell: CoverageSiteCell, month: CoverageColumn | undefined): string => {
    if (cell.note) return cell.note;
    const what = `${site.expects} for ${site.label}${month ? `, ${month.full}` : ""}`;
    if (cell.state === "missing") return `${what}: ${COVERAGE_MEANING.missing.toLowerCase()}`;
    if (cell.state === "unknown") return `${what}: ${COVERAGE_MEANING.unknown.toLowerCase()}`;
    return COVERAGE_MEANING[cell.state];
  };

  const open = (rowId: string, columnId: string) => {
    if (!onOpenReceipt) return;
    const site = sites.find((s) => s.id === rowId);
    const index = months.findIndex((m) => m.id === columnId);
    const receipt = site?.cells[index]?.receipt;
    if (receipt) onOpenReceipt(receipt, `${rowId}:${columnId}`);
  };

  return (
    <CoverageHeatmap
      testId={testId}
      caption={caption}
      columns={months}
      rows={sites.map((site) => ({
        id: site.id,
        label: site.label,
        sublabel: site.expects,
        cells: site.cells.map((cell, index) => ({
          state: cell.state,
          note: noteFor(site, cell, months[index]),
        })),
      }))}
      onCellOpen={onOpenReceipt ? open : undefined}
    />
  );
}
